// node_modules
import {
    createContext,
    RefObject,
    useCallback,
    useEffect,
    useState
} from "react";

type TWindowDimensions = {
    width: number,
    height: number
};

type TDeliveryLayoutContext = {
    isLeftSidebarCollapsed: boolean,
    setIsLeftSidebarCollapsed: (newIsLeftSidebarCollapsedValue: boolean) => void,
    isOnLeftSidebarHoverCollapsed: boolean,
    setIsOnLeftSidebarHoverCollapsed: (newIsOnLeftSidebarHoverCollapsedValue: boolean) => void,
    isRightSidebarCollapsed: boolean,
    setIsRightSidebarCollapsed: (newIsRightSidebarCollapsedValue: boolean) => void,
    isOnRightSidebarHoverCollapsed: boolean,
    setIsOnRightSidebarHoverCollapsed: (newIsOnRightSidebarHoverCollapsedValue: boolean) => void,
    isCommentTileOpen: boolean,
    setIsCommentTileOpen: (newIsCommentTileOpenValue: boolean) => void,
    currentMainScrolledContainer: RefObject<HTMLDivElement>,
    setCurrentMainScrolledContainer: (newCurrentMainScrolledContainer: RefObject<HTMLDivElement>) => void,
    resetScrollCurrentMainScrolledContainer: () => void
};

export const DeliveryLayoutContext = createContext<TDeliveryLayoutContext>({
    isLeftSidebarCollapsed: false,
    setIsLeftSidebarCollapsed: (newIsLeftSidebarCollapsedValue: boolean) => { },
    isOnLeftSidebarHoverCollapsed: false,
    setIsOnLeftSidebarHoverCollapsed: (newIsOnLeftSidebarHoverCollapsedValue: boolean) => { },
    isRightSidebarCollapsed: false,
    setIsRightSidebarCollapsed: (newIsRightSidebarCollapsedValue: boolean) => { },
    isOnRightSidebarHoverCollapsed: false,
    setIsOnRightSidebarHoverCollapsed: (newIsOnRightSidebarHoverCollapsedValue: boolean) => { },
    isCommentTileOpen: false,
    setIsCommentTileOpen: (newIsCommentTileOpenValue: boolean) => { },
    currentMainScrolledContainer: { current: null },
    setCurrentMainScrolledContainer: (newCurrentMainScrolledContainer: RefObject<HTMLDivElement>) => {},
    resetScrollCurrentMainScrolledContainer: () => { }
});

export const DeliveryLayoutProvider = ({ children }: any) => {
    // Logic
    const getWindowDimensions = (): TWindowDimensions => {
        const { innerWidth: width, innerHeight: height } = window;
        return {
            width,
            height
        };
    };

    const handleResize = useCallback((): void => {
        setWindowDimensions(getWindowDimensions());
    }, []);

    // State
    const [isLeftSidebarCollapsed, setIsLeftSidebarCollapsed] = useState(false);
    const [isOnLeftSidebarHoverCollapsed, setIsOnLeftSidebarHoverCollapsed] = useState(false);
    const [isRightSidebarCollapsed, setIsRightSidebarCollapsed] = useState(false);
    const [isOnRightSidebarHoverCollapsed, setIsOnRightSidebarHoverCollapsed] = useState(false);
    const [isCommentTileOpen, setIsCommentTileOpen] = useState(false);
    const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());
    const [currentMainScrolledContainer, setCurrentMainScrolledContainer] = useState<RefObject<HTMLDivElement>>({ current: null });

    // Logic
    useEffect(() => {
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [handleResize]);

    useEffect(() => {
        if (windowDimensions.width < 1081 && !isLeftSidebarCollapsed) {
            setIsLeftSidebarCollapsed(!isLeftSidebarCollapsed);
        }
        if (windowDimensions.width < 1281 && !isRightSidebarCollapsed) {
            setIsRightSidebarCollapsed(!isRightSidebarCollapsed);
        }
    }, [windowDimensions.width, isLeftSidebarCollapsed, isRightSidebarCollapsed]);

    const resetScrollCurrentMainScrolledContainer = useCallback((): void => {
        if (currentMainScrolledContainer.current) {
            currentMainScrolledContainer.current.scrollTop = 0;
        }
    }, [currentMainScrolledContainer]);

    return (
        <DeliveryLayoutContext.Provider
            value={{
                isLeftSidebarCollapsed,
                setIsLeftSidebarCollapsed,
                isOnLeftSidebarHoverCollapsed,
                setIsOnLeftSidebarHoverCollapsed,
                isRightSidebarCollapsed,
                setIsRightSidebarCollapsed,
                isOnRightSidebarHoverCollapsed,
                setIsOnRightSidebarHoverCollapsed,
                isCommentTileOpen,
                setIsCommentTileOpen,
                currentMainScrolledContainer,
                setCurrentMainScrolledContainer,
                resetScrollCurrentMainScrolledContainer
            }}>
            {children}
        </DeliveryLayoutContext.Provider>
    );
};